import {
  ETIQUETA_ETAPA_DORE,
  ETIQUETA_ETAPA_DORE_EN,
  ETIQUETA_FALTANTE_DORE,
  ETIQUETA_FALTANTE_DORE_EN,
  type DetalleDore,
} from '@cnc/contracts';
import {useState, type ReactNode} from 'react';
import {Link, useNavigate, useParams} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {useDetalleDore, useEditarDore, useEliminarDore, useOpcionesDore} from '../api/dore';
import {AYUDA_DORE} from '../componentes/ayuda-dore';
import {CamposDeDore} from '../componentes/campos-de-dore';
import {cambiosDeDore, valoresDeDore, type ValoresDore} from '../componentes/campos-dore';
import {DocumentosDeDore} from '../componentes/documentos-de-dore';
import {EdicionDeMovimientosDore, type SeccionDore} from '../componentes/movimientos-dore';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {usePermisos} from '../componentes/sesion-contexto';
import {et, tr} from '../i18n/idioma';
import {fecha, fechaHora, numero, textoOGuion} from '../utilidades/formato';

// Ficha de una exportacion de dore (decision 0057): las dos ventas (proveedor →
// intermediaria → comprador final), el material, el transporte, los cobros y
// pagos, el ensayo y las liquidaciones. Desde la decision 0094 se edita aqui.
export function PaginaDoreExportacion() {
  const {codigo = ''} = useParams();
  const detalle = useDetalleDore(codigo);
  const d = detalle.data;
  return (
    <section className="space-y-4">
      <Link to="/dore" className="text-sm text-verde hover:underline">
        {tr('← Doré', '← Doré')}
      </Link>
      {detalle.isPending && <p className="text-sm text-apagado">{tr('Cargando…', 'Loading…')}</p>}
      {detalle.isError && <p className="text-sm text-alerta">{detalle.error.message}</p>}
      {d && <Ficha d={d} />}
    </section>
  );
}

const estimada = (valor: string | null, es: boolean) =>
  valor && es ? (
    <span title={tr('Calculada por el archivo, no es la real', 'Calculated by the file, not actual')}>
      ≈ {fecha(valor)}
    </span>
  ) : (
    fecha(valor)
  );
const dinero = (v: number | null) => (v === null ? '—' : `USD ${numero(v, 2)}`);

const BOTON = 'rounded-md border border-linea bg-white px-3 py-1 text-sm hover:bg-papel';
const BOTON_CHICO = 'rounded-md border border-linea bg-white px-2 py-0.5 text-xs font-normal hover:bg-papel';

function Ficha({d}: {d: DetalleDore}) {
  const etapas = et(ETIQUETA_ETAPA_DORE, ETIQUETA_ETAPA_DORE_EN);
  const faltantes = et(ETIQUETA_FALTANTE_DORE, ETIQUETA_FALTANTE_DORE_EN);
  const permisos = usePermisos();
  const [editando, setEditando] = useState<'datos' | SeccionDore | null>(null);
  const tipos: Record<string, string> = {
    anticipo: tr('Primer pago del comprador', 'First payment from the buyer'),
    saldo: tr('Saldo del comprador', 'Balance from the buyer'),
    pago_proveedor: tr('Pago al proveedor', 'Payment to the supplier'),
  };
  // Boton "Editar" de una seccion (solo con permiso y si no se edita otra).
  const editar = (seccion: SeccionDore) =>
    permisos.editar && !editando ? (
      <button type="button" className={BOTON_CHICO} onClick={() => setEditando(seccion)}>
        {tr('✎ Editar', '✎ Edit')}
      </button>
    ) : null;
  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-verde-oscuro">{d.codigo}</h1>
          <p className="text-sm text-apagado">
            {textoOGuion(d.proveedor)} → {textoOGuion(d.intermediaria)} → {textoOGuion(d.compradorFinal)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="rounded-md border border-verde bg-verde-claro px-3 py-1 text-verde-oscuro">
            {etapas[d.etapa]}
          </span>
          {d.estadoArchivo && (
            <span className="rounded-md border border-linea bg-white px-3 py-1 text-apagado">
              {tr('Estado', 'Status')}: {d.estadoArchivo}
            </span>
          )}
          {permisos.editar && !editando && (
            <button type="button" className={BOTON} onClick={() => setEditando('datos')}>
              {tr('✎ Editar datos', '✎ Edit data')}
            </button>
          )}
          {permisos.administrar && !editando && <EliminarDore codigo={d.codigo} />}
        </div>
      </div>
      {d.faltan.length > 0 && (
        <p className="rounded-md border border-alerta/40 bg-red-50 px-3 py-2 text-sm text-alerta" role="status">
          {tr('Falta', 'Missing')}: {d.faltan.map(f => faltantes[f]).join(' · ')}
        </p>
      )}
      {editando === 'datos' ? (
        <EdicionDeDatosDore d={d} onCerrar={() => setEditando(null)} />
      ) : (
        <div className="grid gap-3 lg:grid-cols-3">
          <Tarjeta titulo={tr('Venta', 'Sale')}>
            <Dato nombre={tr('Proveedor', 'Supplier')} campo="proveedor" valor={textoOGuion(d.proveedor)} />
            <Dato
              nombre={tr('Intermediaria', 'Intermediary')}
              campo="intermediaria"
              valor={textoOGuion(d.intermediaria)}
            />
            <Dato
              nombre={tr('Comprador final', 'Final buyer')}
              campo="compradorFinal"
              valor={textoOGuion(d.compradorFinal)}
            />
            <Dato nombre={tr('Referencia', 'Reference')} campo="referencia" valor={textoOGuion(d.referencia)} />
            <Dato nombre={tr('País de destino', 'Destination country')} campo="pais" valor={textoOGuion(d.pais)} />
            <Dato nombre={tr('Compra (NEO)', 'Purchase (NEO)')} campo="compra" valor={textoOGuion(d.compra)} />
            <Dato
              nombre={tr('Venta del proveedor', 'Supplier sale')}
              campo="ventaDelProveedor"
              valor={textoOGuion(d.ventaDelProveedor)}
            />
            <Dato
              nombre={tr('Factura del proveedor', 'Supplier invoice')}
              campo="facturaProveedor"
              valor={textoOGuion(d.facturaProveedor)}
            />
            <Dato
              nombre={tr('Exportación del proveedor', 'Supplier export')}
              campo="exportacionProveedor"
              valor={textoOGuion(d.exportacionProveedor)}
            />
            <Dato
              nombre={tr('Valor al proveedor', 'Supplier value')}
              campo="valorProveedorUsd"
              valor={dinero(d.valorProveedorUsd)}
            />
            <Dato
              nombre={tr('Valor de la venta', 'Sale value')}
              campo="valorVentaUsd"
              valor={dinero(d.valorVentaUsd)}
            />
          </Tarjeta>
          <Tarjeta titulo={tr('Material', 'Material')}>
            <Dato nombre={tr('Barras', 'Bars')} campo="barras" valor={numero(d.barras)} />
            <Dato nombre={tr('Barras de plata', 'Silver bars')} campo="barrasDePlata" valor={numero(d.barrasDePlata)} />
            <Dato nombre={tr('Peso bruto', 'Gross weight')} campo="brutoG" valor={`${numero(d.brutoG, 2)} g`} />
            <Dato nombre={tr('Oro', 'Gold')} campo="oroG" valor={`${numero(d.oroG, 2)} g · ${numero(d.oroOz, 3)} oz`} />
            <Dato
              nombre={tr('Plata', 'Silver')}
              campo="plataG"
              valor={`${numero(d.plataG, 2)} g · ${numero(d.plataOz, 3)} oz`}
            />
            <Dato nombre={tr('Tipo', 'Type')} campo="tipoDeMaterial" valor={textoOGuion(d.tipoDeMaterial)} />
          </Tarjeta>
          <Tarjeta titulo={tr('Fechas, transporte y aduana', 'Dates, transport and customs')}>
            <Dato nombre={tr('Documentos', 'Documents')} campo="fechaDocumentos" valor={fecha(d.fechaDocumentos)} />
            <Dato
              nombre={tr('Exportación', 'Export date')}
              campo="fechaExportacion"
              valor={fecha(d.fechaExportacion)}
            />
            <Dato nombre={tr('Llegada', 'Arrival')} campo="llegada" valor={estimada(d.llegada, d.llegadaEstimada)} />
            <Dato
              nombre={tr('Llegada a la refinería', 'Arrival at refinery')}
              campo="llegadaRefineria"
              valor={estimada(d.llegadaRefineria, d.llegadaRefineriaEstimada)}
            />
            <Dato nombre={tr('Servicio', 'Service')} campo="servicio" valor={textoOGuion(d.servicio)} />
            <Dato
              nombre={tr('Transportadora', 'Carrier')}
              campo="transportadora"
              valor={textoOGuion(d.transportadora)}
            />
            <Dato nombre="MAWB" campo="mawb" valor={textoOGuion(d.mawb)} />
            <Dato nombre="HAWB" campo="hawb" valor={textoOGuion(d.hawb)} />
            <Dato
              nombre={tr('MAWB de conexión', 'Connecting MAWB')}
              campo="mawbConexion"
              valor={textoOGuion(d.mawbConexion)}
            />
            <Dato
              nombre={tr('Radicado VUCE', 'VUCE filing')}
              campo="radicadoVuce"
              valor={textoOGuion(d.radicadoVuce)}
            />
            <Dato
              nombre={tr('Aprobación VUCE', 'VUCE approval')}
              campo="aprobacionVuce"
              valor={fecha(d.aprobacionVuce)}
            />
          </Tarjeta>
        </div>
      )}

      <Tarjeta titulo={tr('Cobros y pagos', 'Collections and payments')} accion={editar('cobros')}>
        {editando === 'cobros' ? (
          <EdicionDeMovimientosDore d={d} seccion="cobros" onCerrar={() => setEditando(null)} />
        ) : d.cobros.length === 0 ? (
          <p className="text-sm text-apagado">
            {tr('Sin cobros ni pagos registrados.', 'No collections or payments.')}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-apagado">
              <tr>
                <th className="py-1 pr-3 font-normal">{tr('Concepto', 'Concept')}</th>
                <th className="py-1 pr-3 text-right font-normal">USD</th>
                <th className="py-1 pr-3 font-normal">{tr('Fecha', 'Date')}</th>
                <th className="py-1 font-normal">{tr('Nota', 'Note')}</th>
              </tr>
            </thead>
            <tbody>
              {d.cobros.map(c => (
                <tr key={c.id} className="border-t border-linea">
                  <td className="py-1 pr-3">
                    {c.direccion === 'cobro' ? '↓ ' : '↑ '}
                    {tipos[c.tipo]}
                  </td>
                  <td className="py-1 pr-3 text-right tabular-nums">{numero(c.montoUsd, 2)}</td>
                  <td className="py-1 pr-3">{estimada(c.fecha, c.fechaEstimada)}</td>
                  <td className="py-1 text-xs text-apagado">
                    {[c.motivoDeDemora, c.comentarios].filter(Boolean).join(' · ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Tarjeta>

      <div className="grid gap-3 lg:grid-cols-2">
        <Tarjeta titulo={tr('Ensayo (assay)', 'Assay')} accion={editar('ensayos')}>
          {editando === 'ensayos' ? (
            <EdicionDeMovimientosDore d={d} seccion="ensayos" onCerrar={() => setEditando(null)} />
          ) : d.ensayos.length === 0 ? (
            <p className="text-sm text-apagado">{tr('Sin ensayo registrado.', 'No assay recorded.')}</p>
          ) : (
            d.ensayos.map(a => (
              <div key={a.id}>
                <Dato
                  nombre={tr('Resultado', 'Result')}
                  campo="resultadoEnsayo"
                  valor={
                    a.resultado === 'aprobado'
                      ? tr('Aprobado', 'Approved')
                      : a.resultado === 'reanalisis'
                        ? tr('Reanálisis', 'Re-assay')
                        : '—'
                  }
                />
                <Dato
                  nombre={tr('Informe', 'Report')}
                  campo="informe"
                  valor={a.informePendiente ? tr('Pendiente', 'Pending') : fecha(a.informeEl)}
                />
                <Dato
                  nombre={tr('Oro del ensayo', 'Assay gold')}
                  campo="ensayoOro"
                  valor={`${numero(a.oroOz, 3)} oz · ${dinero(a.valorOroUsd)}`}
                />
                <Dato
                  nombre={tr('Plata del ensayo', 'Assay silver')}
                  campo="ensayoPlata"
                  valor={`${numero(a.plataOz, 3)} oz · ${dinero(a.valorPlataUsd)}`}
                />
                <Dato
                  nombre={tr('Cargos de la liquidación', 'Settlement charges')}
                  campo="cargos"
                  valor={dinero(a.cargosUsd)}
                />
                <Dato
                  nombre={tr('Reanálisis', 'Re-assay')}
                  campo="reanalisis"
                  valor={a.reanalisis === null ? '—' : a.reanalisis ? tr('Sí', 'Yes') : tr('No', 'No')}
                />
                <Dato
                  nombre={tr('Respuesta del proveedor', 'Supplier answer')}
                  campo="respuestaProveedor"
                  valor={fecha(a.respuestaProveedorEl)}
                />
                <Dato
                  nombre={tr('Respuesta de la refinería', 'Refinery answer')}
                  campo="respuestaRefineria"
                  valor={fecha(a.respuestaRefineriaEl)}
                />
                <Dato
                  nombre={tr('Fin del reanálisis', 'Re-assay end')}
                  campo="finReanalisis"
                  valor={fecha(a.reanalisisFinalEl)}
                />
              </div>
            ))
          )}
        </Tarjeta>
        <Tarjeta titulo={tr('Liquidaciones', 'Settlements')} accion={editar('liquidaciones')}>
          {editando === 'liquidaciones' ? (
            <EdicionDeMovimientosDore d={d} seccion="liquidaciones" onCerrar={() => setEditando(null)} />
          ) : d.liquidaciones.length === 0 ? (
            <p className="text-sm text-apagado">{tr('Sin liquidaciones registradas.', 'No settlements recorded.')}</p>
          ) : (
            d.liquidaciones.map(l => (
              <div key={l.id} className="mb-2">
                <p className="text-xs font-medium text-apagado">
                  {l.lado === 'comprador'
                    ? tr('Comprador final con la intermediaria', 'Final buyer with the intermediary')
                    : tr('Intermediaria con el proveedor', 'Intermediary with the supplier')}
                  {l.pendiente && <span className="ml-2 text-alerta">{tr('pendiente', 'pending')}</span>}
                </p>
                <Dato nombre={tr('Valor', 'Value')} campo="liquidacionValor" valor={dinero(l.valorUsd)} />
                {l.pagableOro !== null && (
                  <Dato
                    nombre={tr('Oro pagable', 'Gold payability')}
                    campo="pagableOro"
                    valor={`${numero(l.pagableOro * 100, 2)} %`}
                  />
                )}
                <Dato nombre={tr('Liquidada el', 'Settled on')} campo="liquidadaEl" valor={fecha(l.liquidadaEl)} />
                <Dato nombre={tr('Aceptada el', 'Accepted on')} campo="aceptadaEl" valor={fecha(l.aceptadaEl)} />
                <Dato nombre={tr('Pago final', 'Final payment')} campo="pagoFinal" valor={fecha(l.pagoFinalEl)} />
                <Dato
                  nombre={tr('Número contable', 'Accounting number')}
                  campo="numeroContable"
                  valor={textoOGuion(l.referenciaContable)}
                />
              </div>
            ))
          )}
        </Tarjeta>
      </div>
      {d.comentarios && editando !== 'datos' && (
        <Tarjeta titulo={tr('Comentarios', 'Comments')}>
          <p className="text-sm whitespace-pre-wrap">{d.comentarios}</p>
        </Tarjeta>
      )}
      <DocumentosDeDore codigo={d.codigo} />
      <p className="text-xs text-apagado">
        {d.origen === 'app'
          ? tr('Creada en la app.', 'Created in the app.')
          : d.filaDelArchivo !== null &&
            tr(
              `Fila ${d.filaDelArchivo} del seguimiento de doré.`,
              `Row ${d.filaDelArchivo} of the doré tracking file.`,
            )}
        {d.editadoEl &&
          tr(
            ` Editada en la app el ${fechaHora(d.editadoEl)}: una carga nueva del seguimiento ya no la cambia.`,
            ` Edited in the app on ${fechaHora(d.editadoEl)}: a new load of the tracking file no longer changes it.`,
          )}
      </p>
    </>
  );
}

// Formulario de los datos de la exportacion: solo envia lo que cambio, con
// la version que se leyo (no pisa el cambio de otra persona).
function EdicionDeDatosDore({d, onCerrar}: {d: DetalleDore; onCerrar: () => void}) {
  const editar = useEditarDore(d.codigo);
  const opciones = useOpcionesDore().data;
  const [iniciales] = useState<ValoresDore>(() => valoresDeDore(d));
  const [valores, setValores] = useState<ValoresDore>(iniciales);
  const [error, setError] = useState<string | null>(null);
  const guardar = async () => {
    setError(null);
    const r = cambiosDeDore(valores, iniciales);
    if ('error' in r) return setError(r.error);
    if (!Object.keys(r.cambios).length) return onCerrar();
    try {
      await editar.mutateAsync({version: d.version, cambios: r.cambios});
      onCerrar();
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : tr('No se pudo guardar.', 'Could not save.'));
    }
  };
  return (
    <form
      aria-label={tr('Editar exportación de doré', 'Edit doré export')}
      className="rounded-lg border border-verde/40 bg-white p-4"
      onSubmit={e => {
        e.preventDefault();
        void guardar();
      }}
    >
      <h2 className="mb-3 font-medium text-verde-oscuro">{tr(`Editar ${d.codigo}`, `Edit ${d.codigo}`)}</h2>
      <CamposDeDore
        valores={valores}
        onCambiar={(campo, valor) => setValores(v => ({...v, [campo]: valor}))}
        opciones={opciones}
      />
      {error && (
        <p className="mt-2 text-sm text-alerta" role="alert">
          {error}
        </p>
      )}
      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={editar.isPending}
          className="rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
        >
          {tr('Guardar cambios', 'Save changes')}
        </button>
        <button type="button" onClick={onCerrar} className={BOTON}>
          {tr('Cancelar', 'Cancel')}
        </button>
      </div>
    </form>
  );
}

// Solo administradores; se puede deshacer desde la bitacora.
function EliminarDore({codigo}: {codigo: string}) {
  const eliminar = useEliminarDore(codigo);
  const navegar = useNavigate();
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <button
        type="button"
        className="rounded-md border border-alerta/40 bg-white px-3 py-1 text-sm text-alerta hover:bg-red-50"
        disabled={eliminar.isPending}
        onClick={() => {
          if (
            !window.confirm(
              tr(
                `¿Eliminar la exportación ${codigo} con sus cobros, ensayos y liquidaciones? Se puede deshacer desde la bitácora.`,
                `Delete export ${codigo} with its collections, assays and settlements? It can be undone from the audit log.`,
              ),
            )
          )
            return;
          eliminar.mutate(undefined, {
            onSuccess: () => void navegar('/dore'),
            onError: e => setError(e.message),
          });
        }}
      >
        {tr('Eliminar', 'Delete')}
      </button>
      {error && (
        <span className="text-sm text-alerta" role="alert">
          {error}
        </span>
      )}
    </>
  );
}

function Tarjeta({titulo, children, accion}: {titulo: string; children: ReactNode; accion?: ReactNode}) {
  return (
    <div className="rounded-lg border border-linea bg-white p-4">
      <h2 className="mb-2 flex items-center justify-between gap-2 font-medium text-verde-oscuro">
        {titulo}
        {accion}
      </h2>
      {children}
    </div>
  );
}

function Dato({nombre, valor, campo}: {nombre: string; valor: ReactNode; campo?: string}) {
  const ayuda = campo ? AYUDA_DORE[campo] : undefined;
  return (
    <div className="flex justify-between gap-3 border-b border-linea/60 py-0.5 text-sm last:border-0">
      <span className="text-apagado">
        {nombre}
        {ayuda && <NotaDeAyuda titulo={nombre} texto={ayuda} />}
      </span>
      <span className="text-right tabular-nums">{valor}</span>
    </div>
  );
}
